/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react';
import { Alert, Button, Form, Modal, Row, Spinner } from 'react-bootstrap';
import Draggable from 'react-draggable';
import { useFetcher, useNavigate } from 'react-router-dom';

import { FormContext } from '@/helper/context';
import { formatErrors } from '@/helper/utility';
import { useStore } from '@/store';

interface Properties {
  title: string;
  url: string;
  putId?: number;
  method?: 'post' | 'put' | 'patch' | 'delete';
  redirect?: string;
  redirectDate?: string;
  size?: 'sm' | 'lg' | 'xl';
  fullscreen?: true | string;
  dialogClassName?: string;
  disabled?: boolean;
  noDisableForm?: boolean;
  noChanges?: boolean;
  beforeSubmit?: (formData: FormData) => { errors?: Record<string, string>; warning?: string; cancel?: boolean } | void;
  onClose?: () => void;
  reset?: () => void;
  draggable?: boolean;
  allowKeepOpen?: boolean;
  changeTrackerRef?: React.MutableRefObject<Record<string, boolean>>;
  children: React.ReactNode;
}

export const ModalForm = React.forwardRef(function ModalForm(
  {
    title,
    url,
    putId,
    method,
    redirect,
    redirectDate,
    size,
    fullscreen,
    dialogClassName,
    disabled,
    noDisableForm = false,
    noChanges = false,
    beforeSubmit,
    onClose,
    reset,
    draggable = false,
    allowKeepOpen = false,
    changeTrackerRef,
    children
  }: Properties,
  reference
) {
  const [errors, setErrors] = React.useState<any>({});
  const [warningMessage, setWarningMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');

  const { setResponse } = useStore();

  const fetcher = useFetcher();
  const [isLoading, setIsLoading] = React.useState(false);
  const [keepOpen, setKeepOpen] = React.useState(false);
  const navigate = useNavigate();
  React.useEffect(() => {
    if (fetcher.state == 'loading' || fetcher.state == 'submitting') {
      setIsLoading(true);
    } else {
      if (fetcher.data?.status == 200) {
        setResponse(fetcher.data.data);
        if (keepOpen) {
          setIsLoading(false);
          setSuccessMessage(fetcher.data.data.message);
          setErrors({});
        } else {
          setModal(false);
        }
        if (redirect) {
          navigate(redirect + '/' + fetcher.data.data.data);
        } else if (redirectDate) {
          navigate(redirectDate + '?d=' + fetcher.data.data.data);
        } else if (typeof reset === 'function') {
          reset();
        }
      } else {
        if (typeof fetcher.data === 'object') {
          setErrors(formatErrors(fetcher.data));
          setSuccessMessage('');
        } else if (typeof fetcher.data === 'string') {
          setWarningMessage(fetcher.data);
        }
        setIsLoading(false);
      }
    }
  }, [fetcher]);

  const [modal, setModal] = React.useState(false);
  React.useImperativeHandle(reference, () => ({
    openModal() {
      setModal(true);
      setWarningMessage('');
      setSuccessMessage('');
      setIsLoading(false);
      setErrors({});
      setKeepOpen(false);
    },
    closeModal() {
      if (!isLoading) {
        setModal(false);
      }
    }
  }));
  const closeModal = () => {
    if (!isLoading) {
      setModal(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    setErrors({});
    setWarningMessage('');
    setSuccessMessage('');

    if (beforeSubmit) {
      const result = beforeSubmit(formData);
      if (result && typeof result === 'object') {
        if (result.errors) {
          setErrors(result.errors);
          return;
        }
        if (result.warning) {
          setWarningMessage(result.warning);
          return;
        }
        if (result.cancel) {
          return;
        }
      }
    }

    fetcher.submit(formData, {
      method: method ?? (putId ? 'put' : 'post'),
      action: url + (putId ? '/' + putId : '')
    });
  };

  const position = React.useRef({ x: 0, y: 0 });
  function handleStop(event: any, data: any) {
    event.preventDefault();
    position.current = { x: data.x, y: data.y };
  }

  const nodeReference = React.useRef(null);

  const internalChangeTracker = React.useRef<Record<string, boolean>>({});
  const changeTracker = changeTrackerRef ?? internalChangeTracker;

  return (
    <Modal
      show={modal}
      onHide={closeModal}
      size={size}
      fullscreen={fullscreen}
      dialogClassName={dialogClassName}
      contentClassName='hide'
      onExiting={onClose}
    >
      <Draggable
        handle='.modal-header'
        nodeRef={nodeReference}
        onStop={handleStop}
        bounds={{ top: -50 }}
        defaultPosition={{ x: position.current.x, y: position.current.y }}
        disabled={!draggable}
      >
        <div ref={nodeReference} className={'modal-content' + (draggable ? ' draggable' : '')}>
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <ModalInner
            isLoading={isLoading}
            disabled={disabled}
            noDisableForm={noDisableForm}
            noChanges={noChanges}
            errors={errors}
            handleSubmit={handleSubmit}
            closeModal={closeModal}
            warningMessage={warningMessage}
            successMessage={successMessage}
            url={url}
            keepOpen={keepOpen}
            setKeepOpen={setKeepOpen}
            allowKeepOpen={allowKeepOpen}
            changeTrackerRef={changeTracker}
          >
            {children}
          </ModalInner>
        </div>
      </Draggable>
    </Modal>
  );
});

interface InnerProperties {
  isLoading: boolean;
  disabled: boolean;
  noDisableForm: boolean;
  noChanges: boolean;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  errors: any;
  closeModal: () => void;
  warningMessage: string;
  successMessage: string;
  url: string;
  keepOpen: boolean;
  setKeepOpen: React.Dispatch<React.SetStateAction<boolean>>;
  allowKeepOpen: boolean;
  changeTrackerRef: React.MutableRefObject<Record<string, boolean>>;
  children: React.ReactNode;
}

function ModalInner({
  isLoading,
  disabled,
  noDisableForm,
  noChanges,
  errors,
  handleSubmit,
  closeModal,
  warningMessage,
  successMessage,
  url,
  keepOpen,
  setKeepOpen,
  allowKeepOpen,
  changeTrackerRef,
  children
}: InnerProperties) {
  const [wasChanged, setWasChanged] = React.useState(false);

  return (
    <React.Fragment>
      <Modal.Body>
        <FormContext.Provider
          value={{
            changeTracker: changeTrackerRef,
            setWasChanged,
            disabled: isLoading || (!noDisableForm && disabled),
            errors
          }}
        >
          <Form id={'form-' + url} onSubmit={handleSubmit} noValidate>
            {warningMessage?.length > 0 && <Alert variant='danger'>{warningMessage}</Alert>}
            {successMessage?.length > 0 && <Alert variant='success'>{successMessage}</Alert>}
            <Row>{children}</Row>
          </Form>
        </FormContext.Provider>
      </Modal.Body>
      <Modal.Footer>
        {allowKeepOpen && (
          <Form.Check
            label='Keep Window Open'
            type='checkbox'
            checked={keepOpen}
            onChange={(event) => setKeepOpen(event.target.checked)}
            className='me-auto text-muted small'
          />
        )}
        <Button variant='secondary' type='button' className='me-2' onClick={closeModal} disabled={isLoading}>
          Cancel
        </Button>
        {isLoading ? (
          <Button variant='primary' type='submit' disabled={true}>
            <Spinner as='span' size='sm' role='status' aria-hidden='true' />
            &nbsp;Loading...
          </Button>
        ) : (
          <Button
            variant='primary'
            type='submit'
            form={'form-' + url}
            disabled={disabled || (!wasChanged && !noChanges)}
          >
            Save
          </Button>
        )}
      </Modal.Footer>
    </React.Fragment>
  );
}
